import * as React from 'react';
import { useContext, useEffect, useState, ReactNode } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logger } from '../services/loggingService';
import { sessionManager } from '../services/sessionManager';
import type { User, UserSubscription, SubscriptionUsage, AuthContextType } from '../types';

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [usage, setUsage] = useState<SubscriptionUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    // Initialize auth session using SessionManager
    const initializeAuth = async () => {
      try {
        // Initialize SessionManager
        const hasSession = await sessionManager.initialize();
        
        if (!hasSession) {
          // No session - user is not logged in
          if (mounted) setLoading(false);
          return;
        }

        // Get session from SessionManager
        const currentSession = sessionManager.getSession();
        if (!currentSession) {
          if (mounted) setLoading(false);
          return;
        }

        if (mounted) {
          setSession(currentSession);
          // Load user data and wait for it to complete before setting loading to false
          try {
            await loadUserData(currentSession.user.id);
            setLoading(false);
          } catch (err) {
            logger.error('Failed to load user data', err);
            setLoading(false); // Still set loading to false even if user data fails
          }
        }
      } catch (error) {
        logger.error('Auth initialization failed', error);
        if (mounted) setLoading(false);
      }
    };

    initializeAuth();

    // Set up auth state listener
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      // Skip INITIAL_SESSION to prevent duplicate loads
      if (event === 'INITIAL_SESSION') return;

      logger.debug('Auth state changed', { event });

      setSession(session);
      
      // Handle token refresh - SessionManager will handle this
      if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Token was refreshed - ensure user stays logged in
        try {
          await loadUserData(session.user.id);
          // Update session statistics
          const stats = sessionManager.getStatistics();
          logger.info('Session refreshed', { 
            refreshCount: stats.refreshCount,
            timeUntilExpiry: stats.timeUntilExpiry 
          });
        } catch (err) {
          logger.error('Failed to load user data on token refresh', err);
        }
        return;
      }

      if (session?.user) {
        try {
          await loadUserData(session.user.id);
        } catch (err) {
          logger.error('Failed to load user data on auth change', err);
        }
      } else if (event === 'SIGNED_OUT') {
        // User explicitly signed out - clear all state
        setUser(null);
        setSubscription(null);
        setUsage(null);
        setUserRole(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const loadUserData = async (userId: string) => {
    try {
      // Get auth user for metadata (cached by SDK, fast) with timeout
      const getUserPromise = supabase.auth.getUser();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('getUser timeout')), 5000)
      );

      const { data: { user: authUser } } = await Promise.race([getUserPromise, timeoutPromise]).catch(() => {
        logger.warn('getUser timed out');
        setLoading(false); // ✅ Ensure loading is false on timeout
        return { data: { user: null } };
      });

      if (!authUser) {
        setLoading(false); // ✅ Ensure loading is false when no user
        return;
      }

      // Extract user role from auth metadata
      const role = authUser.app_metadata?.role || null;
      setUserRole(role);

      // Create fallback user immediately so UI can render
      const fallbackUser: User = {
        id: authUser.id,
        email: authUser.email!,
        full_name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'User',
        avatar_url: authUser.user_metadata?.avatar_url,
        subscription_status: 'trialing',
        current_subscription_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Set fallback user immediately
      setUser(fallbackUser);
      setLoading(false); // UI can render now!

      // Fetch all data in parallel (non-blocking)
      const [userResult, subResult] = await Promise.allSettled([
        supabase.from('users').select('id, email, full_name, avatar_url, subscription_status, current_subscription_id, created_at, updated_at').eq('id', userId).maybeSingle(),
        supabase.from('user_subscriptions').select(`
          id, user_id, plan_id, status, trial_end, current_period_start, current_period_end, stripe_customer_id, stripe_subscription_id, created_at, updated_at,
          subscription_plans(*)
        `).eq('user_id', userId).eq('status', 'active').order('created_at', { ascending: false }).limit(1).maybeSingle()
      ]);

      // Update user if fetched successfully
      if (userResult.status === 'fulfilled' && userResult.value.data) {
        setUser(userResult.value.data);
      } else if (userResult.status === 'fulfilled' && !userResult.value.data && !userResult.value.error) {
        // User doesn't exist in DB, create them (non-blocking)
        supabase.from('users').insert({
          id: authUser.id,
          email: authUser.email!,
          full_name: fallbackUser.full_name,
          avatar_url: fallbackUser.avatar_url,
        }).select().single().then(({ data }) => {
          if (data) setUser(data);
        }).catch(err => {
          logger.warn('Failed to create user record', { error: err.message });
        });
      }

      // Update subscription if fetched successfully
      if (subResult.status === 'fulfilled' && subResult.value.data) {
        setSubscription(subResult.value.data as UserSubscription);

        // Load usage data (non-blocking)
        supabase.from('subscription_usage').select('id, subscription_id, period_start, period_end, projects, agents, tickets, executions, api_calls, created_at, updated_at')
          .eq('subscription_id', subResult.value.data.id)
          .gte('period_end', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
          .then(({ data }) => {
            if (data) setUsage(data);
          })
          .catch(err => {
            logger.warn('Failed to load usage data', { error: err.message });
          });
      }
    } catch (error) {
      logger.error('Error loading user data', error);
      setLoading(false);
    }
  };


  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      logger.error('Error signing up', error);
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Add timeout protection to prevent hanging
      const signInPromise = supabase.auth.signInWithPassword({
        email,
        password,
      });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Sign in timeout')), 15000)
      );

      const { error } = await Promise.race([signInPromise, timeoutPromise]);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      logger.error('Error signing in', error);
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSubscription(null);
      setUsage(null);
    } catch (error) {
      logger.error('Error signing out', error);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/update-password`,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      logger.error('Error resetting password', error);
      return { error: error as Error };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      logger.error('Error updating password', error);
      return { error: error as Error };
    }
  };

  const refreshSubscription = async () => {
    if (user) {
      await loadSubscriptionData(user.id);
    }
  };

  const value = {
    user,
    session,
    subscription,
    usage,
    loading,
    userRole,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
